<template>
  <div v-if="planningInfoError" class="px-[20px] py-3 text-sm text-gray-500 dark:text-gray-400">
    {{ $t('messages.detail.plannings.serviceError') }}
  </div>
  <div v-else-if="planningInfo && !planningInfo.found" class="px-[20px] py-3 text-sm text-gray-500 dark:text-gray-400">
    {{ $t('messages.detail.plannings.noData') }}
  </div>
  <div v-else-if="plan" class="px-[20px] pb-2">
    <div
      v-for="row in planRows"
      :key="row.key"
      class="grid grid-cols-2 py-3 border-b last:border-b-0 border-gray-200 dark:border-gray-700"
    >
      <label class="font-bold">{{ $t('messages.detail.plannings.' + row.key) }}</label>
      <a
        v-if="row.key === 'planName' && plan.planviide"
        :href="plan.planviide"
        target="_blank"
        class="text-primary-600 hover:underline"
      >
        {{ row.value }}
      </a>
      <span v-else>{{ row.value }}</span>
    </div>

    <div
      v-if="planningInfo.landUses.length"
      class="grid grid-cols-2 py-3 border-b last:border-b-0 border-gray-200 dark:border-gray-700"
    >
      <label class="font-bold">{{ $t('messages.detail.plannings.juhtotstarve') }}</label>
      <div>
        <div v-for="landUse in planningInfo.landUses" :key="landUse.tahis ?? landUse.juhtotstarve">
          <span v-if="landUse.tahis" class="font-medium">{{ landUse.tahis }}</span>
          <span v-if="landUse.tahis"> — </span>
          <span>{{ landUse.juhtotstarve }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="planningInfo.landUseSource === 'raster'"
      class="grid grid-cols-2 py-3 border-b last:border-b-0 border-gray-200 dark:border-gray-700"
    >
      <label class="font-bold">{{ $t('messages.detail.plannings.joonis') }}</label>
      <div>
        <a
          v-if="plan.joonis"
          :href="plan.joonis"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary-600 hover:underline"
        >
          {{ $t('messages.detail.plannings.legendOnDrawing') }}
        </a>
        <span v-else>{{ $t('messages.detail.plannings.legendOnDrawing') }}</span>
        <img
          v-if="planningInfo.rasterLegend && plan.planid"
          :src="'/planning/raster-legend/' + plan.planid"
          :alt="$t('messages.detail.plannings.joonis')"
          class="mt-2 max-w-full"
        />
      </div>
    </div>

    <div v-if="planningInfo.otherPlans.length" class="border-b last:border-b-0 border-gray-200 dark:border-gray-700">
      <button
        type="button"
        class="w-full flex items-center gap-2 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        @click="otherPlansExpanded = !otherPlansExpanded"
      >
        <span
          class="pi pi-chevron-right text-primary-500 dark:text-gray-400 transition-transform duration-300 ease-out"
          :class="{ 'rotate-90': otherPlansExpanded }"
        />
        <span class="font-bold">{{ $t('messages.detail.plannings.otherPlans') }}</span>
        <Badge :value="planningInfo.otherPlans.length" size="small" class="bg-primary-600/70 text-white" />
      </button>
      <div
        class="grid transition-[grid-template-rows] duration-300 ease-out"
        :class="otherPlansExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      >
        <div class="overflow-hidden">
          <div
            v-for="otherPlan in planningInfo.otherPlans"
            :key="otherPlan.nimetus + otherPlan.kehtestatudIso"
            class="py-2 pl-7 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800"
          >
            <a
              v-if="otherPlan.planviide"
              :href="otherPlan.planviide"
              target="_blank"
              class="text-primary-600 hover:underline"
            >
              {{ otherPlan.nimetus }}
            </a>
            <span v-else>{{ otherPlan.nimetus }}</span>
            <span class="text-gray-400 dark:text-gray-500"> · {{ otherPlan.kehtestatud }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, shallowRef } from 'vue'
import { useDetailStore } from '@/stores/useDetailStore'

const store = useDetailStore()
const { planningInfo, planningInfoError } = storeToRefs(store)

const otherPlansExpanded = shallowRef(false)

const plan = computed(() => planningInfo.value?.plan ?? null)

const planRows = computed(() => {
  if (!plan.value) {
    return []
  }
  return [
    { key: 'planName', value: plan.value.nimetus },
    { key: 'planType', value: plan.value.liik },
    { key: 'planDate', value: plan.value.kehtestatud },
  ].filter(row => row.value)
})
</script>
